import React, { useCallback, useEffect, useState } from 'react';
import {
  Col, Form, InputNumber, Row, Switch, Select,
} from 'antd';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { capitalize, pick } from 'lodash';
import { Link, Redirect, useHistory } from 'react-router-dom';
import Loader from '../../common/components/Loader';
import useMount from '../../common/hooks/useMount';

import './EventDetails.css';
import {
  fetchGamesForEvents,
  fetchSingleEvent,
  resetEventAuditLogs,
  resetGamesForEvents,
  resetSingleEvent,
  updateEvent,
  updateEventReset,
  markEventForDeletion,
  fetchEvents,
} from '../../redux/actions/eventsActions';
import BusyModal from '../../common/components/BusyModal';
import Portlet from '../../common/components/Portlet';

const GAME_RESTRICTION_PREFIX = 'game-';

const formLayout = {
  labelCol: { span: 11 },
  wrapperCol: {
    span: 10,
    offset: 1,
  },
};

const mapStateToProps = (state) => ({
  event: state.eventsReducer.event,
  isFetching: state.eventsReducer.isFetchingEvent,
  isError: state.eventsReducer.isFetchEventError,
  errorCode: state.eventsReducer.fetchEventErrorStatusCode,

  games: state.eventsReducer.games,
  isFetchingGames: state.eventsReducer.isFetchingGames,
  gamesError: state.eventsReducer.gamesError,

  isUpdatingEvent: state.eventsReducer.isUpdatingEvent,
  isUpdateEventSuccess: state.eventsReducer.isUpdateEventSuccess,
  isUpdateEventError: state.eventsReducer.isUpdateEventError,

  isMarkingForDeletion: state.eventsReducer.isMarkingForDeletion,
  isMarkingForDeletionSuccess: state.eventsReducer.isMarkingForDeletionSuccess,
  isMarkingForDeletionFailure: state.eventsReducer.isMarkingForDeletionError,
});

const mapDispatchToProps = {
  updateEvent,
  updateEventReset,
  fetchSingleEvent,
  fetchGamesForEvents,
  resetSingleEvent,
  resetEventAuditLogs,
  resetGamesForEvents,
  markEventForDeletion,
  fetchEvents,
};

function EventDetails(props) {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showGamesRestrictionsInfo, setShowGamesRestrictionsInfo] = useState(false);
  const history = useHistory();

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleStopEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  useMount(() => {
    if (!props.games.length) {
      props.fetchGamesForEvents();
    }

    if (!props.event) {
      props.fetchSingleEvent({ eventId: props.eventId });
    }

    return () => {
      form.resetFields();
    };
  });

  useEffect(() => {
    if (props.isUpdateEventSuccess) {
      setSubmitted(false);
      handleStopEdit();
    }
  }, [props.isUpdateEventSuccess]);

  useEffect(() => {
    if (props.isError || props.gamesError) {
      props.resetSingleEvent();
      props.resetGamesForEvents();
    }
  }, [props.isError, props.gamesError]);

  useEffect(() => {
    if (props.isMarkingForDeletionSuccess) {
      setSubmitted(false);
      handleStopEdit();
      history.push('/events');
    }
  }, [props.isMarkingForDeletionSuccess]);

  const handleFinish = useCallback(
    (values) => {
      setSubmitted(true);
      const directBodyValues = pick(values, ['points', 'status']);
      console.log('submitting', directBodyValues);
      if (directBodyValues.status === 'delete') {
        console.log('deleting', props.event.name);
        // event is to marked for deletion
        props.markEventForDeletion({ eventName: props.event.name });
        return;
      }

      const event = {
        ...props.event,
        ...directBodyValues,
        restrictionsFirstTime: !!values.firstTime,
        restrictionsGames: props.games.reduce((acc, game) => {
          const value = values[`${GAME_RESTRICTION_PREFIX}${game.gameId}`];

          if (value) {
            return [...acc, Number(game.gameId)];
          }

          return acc;
        }, []),
      };
      console.log('updating event', event);

      props.updateEvent({ event });
    },
    [props.games, props.event],
  );

  const handleFieldsChange = useCallback(
    (_, values) => {
      const gameRestrictionsValues = values.filter((value) => value.name[0].startsWith(GAME_RESTRICTION_PREFIX));

      const allGameRestrictionsSelected = gameRestrictionsValues.every((value) => value.value === true);

      if (!showGamesRestrictionsInfo && allGameRestrictionsSelected) {
        setShowGamesRestrictionsInfo(true);
      }

      if (showGamesRestrictionsInfo && !allGameRestrictionsSelected) {
        setShowGamesRestrictionsInfo(false);
      }
    },
    [showGamesRestrictionsInfo],
  );

  const handleFinishFailed = useCallback(() => {
    setSubmitted(true);
  }, []);

  const handleModalClose = useCallback(() => {
    props.onStopEdit();
    props.updateEventReset();
  }, [props.onStopEdit, props.updateEventReset]);

  if (props.isError || props.gamesError) {
    return <Redirect to={props.originPath} />;
  }

  if (!props.event || !props.games.length) {
    return (
      <Portlet
        config={props.portletConfig}
        activeTab={props.activeTab}
        handleTabClick={props.onTabClick}
        footer={(
          <div className="kt-portlet__foot kt-portlet__foot--lg">
            <div className="row justify-content-start">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link to={props.originPath}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {}}
                >
                  BACK
                </button>
              </Link>
            </div>
          </div>
        )}
      >
        <Loader isLoading />
      </Portlet>
    );
  }

  const restrictionGames = JSON.parse(props.event.restrictions_games.replace(/{/g, '[').replace(/}/g, ']'));
  const restrictionGamesInitialValues = props.games.reduce(
    (acc, game) => ({
      ...acc,
      [`${GAME_RESTRICTION_PREFIX}${game.gameId}`]: restrictionGames.includes(Number(game.gameId)),
    }),
    {},
  );

  const initialValues = {
    ...pick(props.event, ['status', 'points']),
    firstTime: props.event.restrictions_first_time,
    ...restrictionGamesInitialValues,
  };

  if (!form.isFieldsTouched()) {
    handleFieldsChange(
      null,
      Object.keys(initialValues).map((key) => ({
        name: [key],
        value: initialValues[key],
      })),
    );
  }

  return (
    <Portlet
      config={props.portletConfig}
      activeTab={props.activeTab}
      handleTabClick={props.onTabClick}
      footer={
        props.activeTab === 0
        && !(props.isFetching || props.isFetchingGames)
        && (isEditing ? (
          <div className="kt-portlet__foot kt-portlet__foot--lg">
            <div className="row justify-content-end">
              <button
                onClick={handleStopEdit}
                type="button"
                className="btn btn-primary mr-3"
              >
                CANCEL
              </button>
              <button
                onClick={form.submit}
                type="submit"
                className="btn btn-primary"
              >
                SAVE
              </button>
            </div>
          </div>
        ) : (
          <div className="kt-portlet__foot kt-portlet__foot--lg">
            <div className="row justify-content-start">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link to={props.originPath}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {}}
                >
                  BACK
                </button>
              </Link>
            </div>
          </div>
        ))
      }
    >
      <BusyModal
        isLoading={props.isUpdatingEvent || props.isMarkingForDeletion}
        isError={props.isUpdateEventError || props.isMarkingForDeletionFailure}
        onClose={handleModalClose}
        onErrorClose={props.updateEventReset}
      />

      {isEditing ? (
        <span className="font-italic user-select-none">Edit Info</span>
      ) : (
        <button
          className="p-0 bg-transparent btn btn-link text-reset font-weight-bold user-select-none"
          onClick={handleStartEdit}
        >
          <u className="text-primary">Edit Info</u>
        </button>
      )}

      <div className="row event-details">
        <div className="col-lg-12 col-xl-12 order-lg-1 order-xl-1">
          <Loader isLoading={props.isFetchingGames}>
            <Form
              {...formLayout}
              initialValues={initialValues}
              validateTrigger={
                submitted ? ['onSubmit', 'onChange'] : 'onSubmit'
              }
              form={form}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              onFieldsChange={handleFieldsChange}
            >
              <Form.Item label="Event Name">{props.event.name}</Form.Item>
              <Form.Item
                name="points"
                label="Points"
                rules={[
                  isEditing
                    ? { required: true, message: 'Points is required' }
                    : {},
                  { type: 'number', message: 'Points must be a number' },
                  {
                    validator: (_, val) => {
                      const num = Number(val);

                      if (Math.abs(num) > 100) {
                        return Promise.reject(new Error('Points value can be no greater than 100 and no lesser than -100'));
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                {isEditing ? (
                  <InputNumber
                    onPressEnter={form.submit}
                    autoComplete="off"
                    className="event-details__number-input"
                    size="large"
                  />
                ) : (
                  props.event.points
                )}
              </Form.Item>
              <Form.Item name="status" label="Status">
                {isEditing ? (
                  <Select
                    options={[
                      {
                        value: 'active',
                        label: 'Active',
                      },
                      {
                        value: 'inactive',
                        label: 'Inactive',
                      },
                      {
                        value: 'delete',
                        label: 'Delete',
                      },
                    ]}
                  />
                ) : (
                  capitalize(props.event.status)
                )}
              </Form.Item>

              <Row>
                <Col span={10}>
                  <h3 className="event-details__restrictions-header text-sm-right">
                    Restrictions
                  </h3>
                </Col>
              </Row>

              <Form.Item
                name="firstTime"
                label="First Time"
                className="event-details__game-restrictions"
                valuePropName="checked"
                colon={false}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {isEditing ? (
                  <Switch />
                ) : props.event.restrictions_first_time ? (
                  <span className="text-success">Enabled</span>
                ) : (
                  <span className="text-danger">Disabled</span>
                )}
              </Form.Item>

              <Row>
                <Col span={11}>
                  <h4
                    className={classNames(
                      'event-details__restrictions-header event-details__restrictions-games-header',
                      'text-sm-right mr-sm-3',
                    )}
                  >
                    Games
                  </h4>
                </Col>
              </Row>

              {props.games.map((game) => (
                <Form.Item
                  key={game.gameId}
                  name={`${GAME_RESTRICTION_PREFIX}${game.gameId}`}
                  label={game.brandName}
                  className="event-details__game-restriction"
                  valuePropName="checked"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 9, offset: 1 }}
                  colon={false}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {isEditing ? (
                    <Switch />
                  ) : restrictionGames.includes(Number(game.gameId)) ? (
                    <span className="text-success">Enabled</span>
                  ) : (
                    <span className="text-danger">Disabled</span>
                  )}
                </Form.Item>
              ))}
              {isEditing && showGamesRestrictionsInfo && (
                <Row>
                  <Col xs={{ span: 24 }} sm={{ span: 9, offset: 13 }}>
                    <p>
                      Enabling all games will denote no restrictions, and act
                      the same as if all games are disabled
                    </p>
                  </Col>
                </Row>
              )}
            </Form>
          </Loader>
        </div>
      </div>
    </Portlet>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(EventDetails);
